import { absoluteValue, toStringBigDecimal } from '@0xtorch/big-decimal'
import type { Action } from '@0xtorch/core'
import { createEvmActionSource, isHex } from '@0xtorch/evm'
import { createSolanaActionSource } from '@0xtorch/solana'
import {
  ApiPassphraseRequiredException,
  ApiSecretRequiredException,
  AssetNotFoundException,
} from '../../errors'
import type { FetchApiData } from '../../types'
import { createGetAsset } from '../../utils/createGetAsset'
import {
  bitgetAccountCategory,
  networkEvmChainIds,
  solanaNetwork,
} from '../constants'
import { createClient } from '../createClient'
import { responseSchema } from './schema'

export const fetchApiData: FetchApiData = async ({
  apiKey,
  apiSecret,
  apiPassphrase,
  assets,
  config,
  from,
  createHeaders,
  proxy,
  symbolAssetMap,
  to,
}) => {
  if (!apiSecret) {
    throw new ApiSecretRequiredException()
  }
  if (!apiPassphrase) {
    throw new ApiPassphraseRequiredException()
  }

  const getAsset = createGetAsset({ assets, symbolAssetMap })

  const client = createClient({
    apiKey,
    apiSecret,
    apiPassphrase,
    createHeaders,
    proxy,
  })

  const response = await client.run({
    method: 'GET',
    path: '/api/v2/spot/wallet/withdrawal-records',
    query: {
      ...(from ? { startTime: from.toString() } : {}),
      ...(to ? { endTime: to.toString() } : {}),
      limit: config.limit.toString(),
    },
    responseSchema,
  })

  // Sort by cTime in ascending order
  const sortedWithdrawals = response.data.sort(
    (a, b) => Number(a.cTime) - Number(b.cTime),
  )

  // Process data
  let stopped = false
  let fetchedFrom: number | undefined
  let fetchedTo: number | undefined
  const sources: string[] = []
  const actions: Action[] = []

  for (const item of sortedWithdrawals) {
    // Skip failed withdrawals
    if (item.status === 'fail') {
      continue
    }

    // Stop processing if withdrawal is pending
    if (item.status === 'pending') {
      stopped = true
      break
    }

    const timestamp = Number(item.cTime)

    // Update fetchedFrom and fetchedTo
    fetchedFrom =
      fetchedFrom === undefined ? timestamp : Math.min(fetchedFrom, timestamp)
    fetchedTo =
      fetchedTo === undefined ? timestamp : Math.max(fetchedTo, timestamp)

    const chain = item.chain.includes('(')
      ? item.chain.split('(')[1].split(')')[0]
      : item.chain

    // Create fee action if fee !== 0
    if (item.fee.value !== 0n) {
      const asset = getAsset(item.coin)
      if (!asset) {
        throw new AssetNotFoundException(item.coin)
      }

      actions.push({
        comment: `Withdraw ${toStringBigDecimal(item.size)} [${item.coin}] | chain: ${chain} | tx: ${item.tradeId}`,
        evidence: 'system-rule',
        order: 0,
        source: `${bitgetAccountCategory}_${config.type}_${item.orderId}`,
        type: 'fee',
        timestamp,
        transfers: [
          {
            asset,
            amount: absoluteValue(item.fee),
            direction: 'out',
          },
        ],
      })
    }

    // Create EVM source if applicable
    if (chain && item.tradeId) {
      const evmChainId = networkEvmChainIds[chain]
      if (evmChainId && isHex(item.tradeId)) {
        sources.push(
          createEvmActionSource({
            chainId: evmChainId,
            transactionHash: item.tradeId,
          }),
        )
      }
    }

    // Create Solana source if applicable
    if (chain === solanaNetwork && item.tradeId) {
      sources.push(createSolanaActionSource({ signature: item.tradeId }))
    }
  }

  // Sort actions by timestamp in descending order
  const sortedActions = actions.sort((a, b) => b.timestamp - a.timestamp)

  return {
    actions: sortedActions,
    count: response.data.length,
    from: fetchedFrom,
    sources,
    stopped,
    to: fetchedTo,
  }
}
